import {
  Object3D,
  Plane,
  PlaneHelper,
  Quaternion,
  Raycaster,
  Vector2,
  Vector3,
} from "three";
import { SCENE_TYPE } from "../../config/ViewerConfig";
import { ICameraBehavior } from "../../utils/camera-utils";
import { Viewer } from "../../viewer";
import { GenericPanControl } from "../controls/pan-controls/generic-pan-control";
import { IPanArguments } from "../controls/pan-controls/pan-control-interface";
import {
  GenericCustomRotateControl,
  ICustomRotateArguments,
} from "../controls/rotate-controls/generic-custom-rotate-control";
import { IOrbitRotateArguments } from "../controls/rotate-controls/generic-orbit-rotate-control";
import {
  IOrthographicFixedPointZoomArguments,
  OrthographicFixedPointZoomControl,
} from "../controls/zoom-controls/orthographic-fixed-point-zoom-control";
import { Quick3DOrthographicCamera } from "../quick3d-orthographic-camera";

export class OrthographicCustomBehavior implements ICameraBehavior {
  private _viewer: Viewer;
  private camera: Quick3DOrthographicCamera;
  private _deltaNDC: Vector2;
  private panControl: GenericPanControl;
  private panControlArguments: IPanArguments;
  private rotateControl: GenericCustomRotateControl;
  private rotateControlArguments: ICustomRotateArguments;
  private targetPoint: Object3D;
  private worldOrientation: Quaternion;
  private zoomControl: OrthographicFixedPointZoomControl;
  private zoomControlArguments: IOrthographicFixedPointZoomArguments;
  private _panPlaneHelper: PlaneHelper;

  public panSpeed = 0;

  private panPlane: Plane;
  private rayCaster: Raycaster;

  public get rotateSpeed(): number {
    return this.rotateControlArguments.rotateSpeed as number;
  }
  // public get panSpeed(): number { return 0; }
  public get zoomSpeed(): number {
    return this.zoomControlArguments.zoomStep as number;
  }

  // public set panSpeed(_val: number) { }
  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  public set rotateSpeed(val: number) {
    this.rotateControlArguments.rotateSpeed = val;
  }
  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  public set zoomSpeed(val: number) {
    this.zoomControlArguments.zoomStep = val;
  }

  public setDeltaNDC(val: Vector2) {
    this._deltaNDC = val;
  }
  public alternativeWheel: boolean;

  constructor(
    camera: Quick3DOrthographicCamera,
    targetPoint: Object3D,
    worldOrientation: Quaternion,
    viewer: Viewer
  ) {
    this._viewer = viewer;
    this.camera = camera;
    this._deltaNDC = new Vector2();
    this.panControl = new GenericPanControl();
    this.panControlArguments = {} as IPanArguments;
    this.rotateControl = new GenericCustomRotateControl();
    this.rotateControlArguments = {
      target: new Vector3(),
    } as IOrbitRotateArguments;
    this.targetPoint = targetPoint;
    this.worldOrientation = worldOrientation;
    this.zoomControl = new OrthographicFixedPointZoomControl();
    this.zoomControlArguments = { zoomStep: 0.1 };
    this.panPlane = new Plane();
    this.rayCaster = new Raycaster();
  }

  public start(ndcX: number, ndcY: number): void {
    this._deltaNDC.set(ndcX, ndcY);
    this.rayCaster.setFromCamera(new Vector3(ndcX, ndcY, 0), this.camera);
    const cameraDirection = new Vector3();
    this.camera.getWorldDirection(cameraDirection);
    const distance = Math.max(
      this.camera.position.distanceTo(this._viewer.getCenter()),
      1
    );
    const planePosition = this.camera.position
      .clone()
      .add(cameraDirection.clone().multiplyScalar(distance));
    this.panPlane.setFromNormalAndCoplanarPoint(cameraDirection, planePosition);
  }

  public rotate(ndcX: number, ndcY: number): void {
    // this.rotateControlArguments.rotateSpeed = this._viewer.testUIVal[1];
    this.rotateControlArguments.rotateSpeed = 1.3;

    // console.log(`rotate speed = ${this.rotateControlArguments.rotateSpeed}`);
    this.rotateControlArguments.horizontalAngle = ndcX - this._deltaNDC.x;
    this.rotateControlArguments.verticalAngle = ndcY - this._deltaNDC.y;
    this.rotateControlArguments.target?.copy(this.targetPoint.position);
    this.rotateControlArguments.center?.copy(this._viewer.getCenter());

    this.targetPoint.applyQuaternion(this.worldOrientation.invert());
    this.rotateControl.rotate(this.camera, this.rotateControlArguments);
    this.targetPoint.applyQuaternion(this.worldOrientation.invert());
    this.targetPoint.updateMatrixWorld(true);
    this._deltaNDC.set(ndcX, ndcY);
  }

  public setRotateCenter(ndcX: number, ndcY: number): Vector3 {
    this.rayCaster.setFromCamera(new Vector2(ndcX, ndcY), this.camera);
    const newPointUnderMouse = new Vector3();
    this.rayCaster.ray.intersectPlane(this.panPlane, newPointUnderMouse);
    this.targetPoint.position.copy(newPointUnderMouse);

    // center
    this.rayCaster.setFromCamera(new Vector2(0, 0), this.camera);
    const center = new Vector3();
    this.rayCaster.ray.intersectPlane(this.panPlane, center);
    this.rotateControlArguments.center?.copy(center);
    return newPointUnderMouse;
  }

  public pan(ndcX: number, ndcY: number): void {
    const scaleX = this.camera.width / this.camera.zoom;
    const scaleY = this.camera.height / this.camera.zoom;
    this.panControlArguments.normalizedDirectionX =
      (this._deltaNDC.x - ndcX) * scaleX;
    this.panControlArguments.normalizedDirectionY =
      (this._deltaNDC.y - ndcY) * scaleY;

    this.panControlArguments.panSpeed = 0.5;
    // this.panControlArguments.panSpeed = this._viewer.testUIVal[0];

    // console.log(`pan speed = ${this.panControlArguments.panSpeed}`);
    this.panControl.pan(this.camera, this.panControlArguments);
    this._deltaNDC.set(ndcX, ndcY);
  }

  public zoom(delta: number, ndcX?: number, ndcY?: number): void {
    this.zoomControlArguments.zoomDirection = (delta / Math.abs(delta)) as
      | 1
      | -1;
    this.zoomControlArguments.ndcPointX = this._deltaNDC.x;
    this.zoomControlArguments.ndcPointY = this._deltaNDC.y;
    this.zoomControl.zoom(this.camera, this.zoomControlArguments);
  }

  public getZoomDirection(ndcX: number, ndcY: number): number {
    return this._deltaNDC.y - ndcY < 0 ? 1 : -1;
  }

  public wheel(ndcX: number, ndcY: number, wheelDirection: 1 | -1): void {
    this.zoomControlArguments.zoomDirection = wheelDirection;
    this.zoomControlArguments.ndcPointX = ndcX;
    this.zoomControlArguments.ndcPointY = ndcY;
    this.zoomControlArguments.zoomStep = 0.06 / (this.alternativeWheel ? 2 : 1);

    // console.log(`zoom step = ${this.zoomControlArguments.zoomStep}`);
    this.zoomControl.zoom(this.camera, this.zoomControlArguments);
    this._deltaNDC.set(ndcX, ndcY);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public end(): void {
    console.log("end");
    this._viewer.getScene(SCENE_TYPE.FOREGROUND).remove(this._panPlaneHelper);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public update(): void {}

  public flatZoom(delta: 1 | -1): void {
    this.wheel(0, 0, delta);
  }

  public flatPan(ndcDeltaX: number, ndcDeltaY: number): void {
    this.pan(this._deltaNDC.x + ndcDeltaX, this._deltaNDC.y + ndcDeltaY);
  }
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public flatRotate(): void {}

  public addHelper(): void {
    const rateConst = 1 / 3;

    this._panPlaneHelper = new PlaneHelper(
      this.panPlane,
      this._viewer.getRootLength() * rateConst
    );
    this._viewer.getScene(SCENE_TYPE.FOREGROUND).add(this._panPlaneHelper);
  }

  public setHelperVisibility(visible: boolean) {
    visible
      ? this._viewer.getScene(SCENE_TYPE.FOREGROUND)?.add(this._panPlaneHelper)
      : this._viewer
          .getScene(SCENE_TYPE.FOREGROUND)
          ?.remove(this._panPlaneHelper);
  }
}
